import {
    Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete,
    CircularProgress, Snackbar, Alert, IconButton, Avatar, AvatarGroup, InputAdornment, Tooltip,
    Chip, ToggleButtonGroup, ToggleButton, FormControlLabel, Switch
} from '@mui/material'
import {
    Close, Search as SearchIcon, Edit, PersonAddAlt1, RateReview, Place,
    CheckCircleOutline, CancelOutlined, VisibilityOutlined, VisibilityOffOutlined
} from '@mui/icons-material'
import { FC, useCallback, useEffect, useState } from 'react'
import EmptyView from '../common/EmptyView'
import Show from '../common/show'
import { Api, numberWithCommas } from '../../helpers'
import _ from 'lodash'
import { Connect, mapStateToProps } from '../../Redux'
import { UserProps } from '../../types/user'

type ApprovalStatus =
    | 'Draft' | 'Awaiting Verification' | 'Verification Assigned' | 'Verification Completed'
    | 'Pending Review' | 'Published' | 'Rejected' | 'Unpublished'
type PaymentStatus = 'Unpaid' | 'Payment Pending' | 'Paid'
type StatusFilter = 'All' | ApprovalStatus
type TrafficRating = 'Average Player Traffic' | 'Good Player Traffic' | 'Best Player Traffic'

interface SpaceListing {
    _id: string
    title: string
    description: string
    location: string
    country?: string
    latitude?: number
    longitude?: number
    monthlyRent: number
    currency?: string
    images: string[]
    sizeValue?: number
    amenities?: string
    isAvailable?: boolean
    availabilityNote?: string
    paymentStatus: PaymentStatus
    listingFeeAmount?: number
    feeRefundedAt?: string
    approvalStatus: ApprovalStatus
    rejectionReason?: string
    assignedVerifierId?: string
    assignedVerifierName?: string
    verifierAssignedAt?: string
    verifiedAt?: string
    verificationNotes?: string
    verificationPhotos?: string[]
    trafficRating?: TrafficRating
    verificationRejectionReason?: string
}

interface StaffOption {
    _id: string
    name: string
    phone: string
    active: boolean
}

const TRAFFIC_DOT: Record<TrafficRating, string> = {
    'Average Player Traffic': '🟡',
    'Good Player Traffic': '🟢',
    'Best Player Traffic': '🟢'
}

interface FormState {
    title: string
    description: string
    location: string
    country: string
    monthlyRent: string
    sizeValue: string
    amenities: string
    isAvailable: boolean
    availabilityNote: string
    images: string[]
}

interface StateProps {
    spaces: SpaceListing[]
    loading: boolean
    searchVal: string
    statusFilter: StatusFilter
    showModal: boolean
    editingSpaceId: string | null
    updatingId: string | null
    saving: boolean
    form: FormState
    error: string | null
    previewSpace: SpaceListing | null
    rejectingSpace: SpaceListing | null
    rejectionReason: string
    assigningSpace: SpaceListing | null
    staffOptions: StaffOption[]
    selectedStaff: StaffOption | null
    assigning: boolean
    reviewingSpace: SpaceListing | null
    verificationRejectMode: boolean
    verificationRejectionReason: string
}

const defaultForm: FormState = {
    title: '', description: '', location: '', country: '', monthlyRent: '',
    sizeValue: '', amenities: '', isAvailable: true, availabilityNote: '', images: []
}

const STATUS_FILTERS: StatusFilter[] = [
    'All', 'Draft', 'Awaiting Verification', 'Verification Assigned', 'Verification Completed',
    'Pending Review', 'Published', 'Rejected', 'Unpublished'
]
const STATUS_COLOR: Record<ApprovalStatus, { bg: string; color: string }> = {
    'Draft': { bg: '#f3f4f6', color: '#6b7280' },
    'Awaiting Verification': { bg: '#e0e7ff', color: '#3730a3' },
    'Verification Assigned': { bg: '#e0e7ff', color: '#3730a3' },
    'Verification Completed': { bg: '#fef9c3', color: '#854d0e' },
    'Pending Review': { bg: '#fef9c3', color: '#854d0e' },
    'Published': { bg: '#dcfce7', color: '#166534' },
    'Rejected': { bg: '#fee2e2', color: '#991b1b' },
    'Unpublished': { bg: '#f3f4f6', color: '#6b7280' }
}
const PAYMENT_COLOR: Record<PaymentStatus, { bg: string; color: string }> = {
    'Unpaid': { bg: '#fee2e2', color: '#991b1b' },
    'Payment Pending': { bg: '#fef9c3', color: '#854d0e' },
    'Paid': { bg: '#dcfce7', color: '#166534' }
}
const headers = ['Title', 'Details', 'Location', 'Rent', 'Images', 'Payment', 'Status', 'Player Traffic', 'Actions']

interface Props {
    User?: UserProps
}

// The admin moderation table for "Spaces for Hire" listings - mirrors Products/index.tsx's shape
// closely (same table/dialog structure), minus the Item Types tab (spaces have no category
// picker) and minus an admin-authored "add space" path (only owners post spaces, via My Shop -
// see spaceHire.ts). The one real divergence: a payment status column/gate, since a space (unlike
// a product) can be Pending Review while still unpaid, and approveSpace on the server refuses
// that combination outright.
const Spaces: FC<Props> = ({ User }) => {
    const currency = User?.currency ?? 'KES'
    const [state, setState] = useState<StateProps>({
        spaces: [],
        loading: true,
        searchVal: '',
        statusFilter: 'All',
        showModal: false,
        editingSpaceId: null,
        updatingId: null,
        saving: false,
        form: defaultForm,
        error: null,
        previewSpace: null,
        rejectingSpace: null,
        rejectionReason: '',
        assigningSpace: null,
        staffOptions: [],
        selectedStaff: null,
        assigning: false,
        reviewingSpace: null,
        verificationRejectMode: false,
        verificationRejectionReason: ''
    })
    const {
        spaces, loading, searchVal, statusFilter, showModal, editingSpaceId,
        updatingId, saving, form, error, previewSpace, rejectingSpace, rejectionReason,
        assigningSpace, staffOptions, selectedStaff, assigning,
        reviewingSpace, verificationRejectMode, verificationRejectionReason
    } = state

    const setForm = (patch: Partial<FormState>) =>
        setState(s => ({ ...s, form: { ...s.form, ...patch } }))

    const getSpaces = (search?: string, status: StatusFilter = statusFilter) => {
        setState(s => ({ ...s, loading: true }))
        Api({
            controller: 'Spaces',
            action: 'getAllSpacesAdmin',
            searchVal: search,
            ...(status !== 'All' && { approvalStatus: status })
        })
            .then((resp: any) => setState(s => ({ ...s, spaces: resp, loading: false })))
            .catch(() => setState(s => ({ ...s, loading: false })))
    }

    const debouncedSearch = useCallback(
        _.debounce((val: string) => getSpaces(val || undefined), 700),
        [statusFilter]
    )

    const handleStatusFilter = (_: any, val: StatusFilter | null) => {
        if (!val) return
        setState(s => ({ ...s, statusFilter: val }))
        getSpaces(searchVal || undefined, val)
    }

    const openEdit = (space: SpaceListing) =>
        setState(s => ({
            ...s,
            showModal: true,
            editingSpaceId: space._id,
            form: {
                title: space.title,
                description: space.description ?? '',
                location: space.location ?? '',
                country: space.country ?? '',
                monthlyRent: String(space.monthlyRent),
                sizeValue: space.sizeValue ? String(space.sizeValue) : '',
                amenities: space.amenities ?? '',
                isAvailable: space.isAvailable !== false,
                availabilityNote: space.availabilityNote ?? '',
                images: space.images ?? []
            }
        }))

    const runModeration = (action: 'approveSpace' | 'publishSpace' | 'unpublishSpace', spaceId: string) => {
        setState(s => ({ ...s, updatingId: spaceId }))
        Api({ controller: 'Spaces', action, spaceId })
            .then((resp: any) => {
                setState(s => ({ ...s, updatingId: null, ...(resp?.error && { error: resp.error.message }) }))
                if (!resp?.error) getSpaces(searchVal || undefined)
            })
            .catch((err: any) => setState(s => ({ ...s, updatingId: null, error: err?.message || 'Something went wrong.' })))
    }

    const confirmReject = () => {
        if (!rejectingSpace || !rejectionReason.trim()) return
        setState(s => ({ ...s, updatingId: rejectingSpace._id }))
        Api({ controller: 'Spaces', action: 'rejectSpace', spaceId: rejectingSpace._id, rejectionReason })
            .then((resp: any) => {
                if (resp?.error)
                    setState(s => ({ ...s, updatingId: null, error: resp.error.message }))
                else {
                    setState(s => ({ ...s, updatingId: null, rejectingSpace: null, rejectionReason: '' }))
                    getSpaces(searchVal || undefined)
                }
            })
            .catch((err: any) => setState(s => ({ ...s, updatingId: null, error: err?.message || 'Failed to reject space.' })))
    }

    // Assign a verifier - same Autocomplete-over-MarketplaceStaff pattern as Orders/index.tsx's
    // openAssign/confirmAssign (same roster, reused for a different job).
    const openAssignVerifier = (space: SpaceListing) => {
        setState(s => ({ ...s, assigningSpace: space, selectedStaff: null }))
        Api({ controller: 'MarketplaceStaff', action: 'getMarketplaceStaff' })
            .then((resp: any) => setState(s => ({ ...s, staffOptions: (resp || []).filter((m: StaffOption) => m.active) })))
            .catch(() => setState(s => ({ ...s, staffOptions: [] })))
    }

    const confirmAssignVerifier = () => {
        if (!assigningSpace || !selectedStaff) return
        setState(s => ({ ...s, assigning: true }))
        Api({ controller: 'Spaces', action: 'assignSpaceVerifier', spaceId: assigningSpace._id, staffPhone: selectedStaff.phone })
            .then((resp: any) => {
                if (resp?.error)
                    setState(s => ({ ...s, assigning: false, error: resp.error.message }))
                else {
                    setState(s => ({ ...s, assigning: false, assigningSpace: null, selectedStaff: null }))
                    getSpaces(searchVal || undefined)
                }
            })
            .catch((err: any) => setState(s => ({ ...s, assigning: false, error: err?.message || 'Failed to assign verifier.' })))
    }

    const confirmApproveVerification = () => {
        if (!reviewingSpace) return
        setState(s => ({ ...s, updatingId: reviewingSpace._id }))
        Api({ controller: 'Spaces', action: 'approveVerification', spaceId: reviewingSpace._id })
            .then((resp: any) => {
                if (resp?.error)
                    setState(s => ({ ...s, updatingId: null, error: resp.error.message }))
                else {
                    setState(s => ({ ...s, updatingId: null, reviewingSpace: null }))
                    getSpaces(searchVal || undefined)
                }
            })
            .catch((err: any) => setState(s => ({ ...s, updatingId: null, error: err?.message || 'Failed to approve verification.' })))
    }

    const confirmRejectVerification = () => {
        if (!reviewingSpace || !verificationRejectionReason.trim()) return
        setState(s => ({ ...s, updatingId: reviewingSpace._id }))
        Api({ controller: 'Spaces', action: 'rejectVerification', spaceId: reviewingSpace._id, verificationRejectionReason })
            .then((resp: any) => {
                if (resp?.error)
                    setState(s => ({ ...s, updatingId: null, error: resp.error.message }))
                else {
                    setState(s => ({ ...s, updatingId: null, reviewingSpace: null, verificationRejectMode: false, verificationRejectionReason: '' }))
                    getSpaces(searchVal || undefined)
                }
            })
            .catch((err: any) => setState(s => ({ ...s, updatingId: null, error: err?.message || 'Failed to send verification back.' })))
    }

    const saveSpace = async () => {
        if (!editingSpaceId) return
        setState(s => ({ ...s, saving: true }))
        try {
            const resp: any = await Api({
                controller: 'Spaces',
                action: 'updateSpace',
                spaceId: editingSpaceId,
                title: form.title,
                description: form.description,
                location: form.location,
                country: form.country,
                monthlyRent: Number(form.monthlyRent),
                sizeValue: form.sizeValue ? Number(form.sizeValue) : undefined,
                amenities: form.amenities,
                isAvailable: form.isAvailable,
                availabilityNote: form.availabilityNote,
                images: form.images
            })

            if (resp?.error)
                setState(s => ({ ...s, saving: false, error: resp.error.message }))
            else {
                setState(s => ({ ...s, saving: false, showModal: false, editingSpaceId: null, form: defaultForm }))
                getSpaces(searchVal || undefined)
            }
        } catch (err: any) {
            setState(s => ({ ...s, saving: false, error: err?.message || 'Something went wrong.' }))
        }
    }

    const canSave = !saving && !!form.title && !!form.description && !!form.location && !!form.monthlyRent

    useEffect(() => { getSpaces() }, [])

    return (
        <Box>
            <Box sx={{ padding: 2, textAlign: 'left' }}>
                <Typography sx={{ color: '#000', fontSize: 25, fontWeight: 'bold' }}>Spaces for Hire</Typography>
                <Typography sx={{ color: '#6b7280', fontSize: 14 }}>
                    Owners post a space and pay a KES 2,000 listing fee (now or later) before it enters review - only a Paid, Published listing appears on the Marketplace.
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '5px', mb: 1, flexWrap: 'wrap' }}>
                <TextField
                    size='small'
                    placeholder='Search spaces...'
                    value={searchVal}
                    onChange={e => {
                        const val = e.target.value
                        setState(s => ({ ...s, searchVal: val }))
                        debouncedSearch(val)
                    }}
                    sx={{ flex: 1, minWidth: 200, bgcolor: '#fff', borderRadius: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon sx={{ fontSize: 20, color: '#9ca3af' }} />
                            </InputAdornment>
                        )
                    }}
                />
            </Box>

            <Box sx={{ px: '5px', mb: 2 }}>
                <ToggleButtonGroup
                    value={statusFilter}
                    exclusive
                    onChange={handleStatusFilter}
                    size='small'
                    sx={{ bgcolor: '#fff', borderRadius: 2, flexWrap: 'wrap' }}
                >
                    {STATUS_FILTERS.map(f => (
                        <ToggleButton
                            key={f}
                            value={f}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                px: 2,
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: '#fff',
                                    '&:hover': { bgcolor: 'primary.dark' }
                                }
                            }}
                        >
                            {f}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            <Show>
                <Show.When isTrue={loading}>
                    <EmptyView loading={loading} oopTxt='No Spaces' text='No spaces found.' />
                </Show.When>
                <Show.Else>
                    <TableContainer component={Paper} sx={{ mt: 1, mx: '5px' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.main' }}>
                                    {headers.map(h => (
                                        <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {spaces.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={headers.length} align='center' sx={{ color: '#9ca3af', py: 4 }}>
                                            No spaces listed yet.
                                        </TableCell>
                                    </TableRow>
                                ) : spaces.map(space => {
                                    const status = space.approvalStatus || 'Draft'
                                    const paymentStatus = space.paymentStatus || 'Unpaid'
                                    const canApprove = status === 'Pending Review' && paymentStatus === 'Paid'
                                    return (
                                        <TableRow key={space._id} hover>
                                            <TableCell sx={{ fontWeight: 600 }}>{space.title}</TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontSize: '0.8rem', color: '#374151' }}>{space.location || '—'}</Typography>
                                                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                                    {space.sizeValue ? `${space.sizeValue} sq ft · ` : ''}{space.isAvailable !== false ? 'Available' : 'Not available'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {typeof space.latitude === 'number' && typeof space.longitude === 'number' ? (
                                                    <Tooltip title={`${space.latitude}, ${space.longitude}`}>
                                                        <Button
                                                            size='small'
                                                            startIcon={<Place fontSize='small' />}
                                                            href={`https://www.google.com/maps?q=${space.latitude},${space.longitude}`}
                                                            target='_blank'
                                                            rel='noopener noreferrer'
                                                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                                        >
                                                            View on map
                                                        </Button>
                                                    </Tooltip>
                                                ) : <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</Typography>}
                                            </TableCell>
                                            <TableCell>{space.currency ?? currency} {numberWithCommas(space.monthlyRent)}/mo</TableCell>
                                            <TableCell>
                                                {space.images?.length ? (
                                                    <Box
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                                                        onClick={() => setState(s => ({ ...s, previewSpace: space }))}
                                                    >
                                                        <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 32, height: 32, fontSize: '0.65rem', border: '2px solid #fff' } }}>
                                                            {space.images.map((src, i) => (
                                                                <Avatar key={i} src={src} variant='rounded' sx={{ width: 32, height: 32 }} />
                                                            ))}
                                                        </AvatarGroup>
                                                    </Box>
                                                ) : (
                                                    <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={space.feeRefundedAt ? `Fee refunded ${new Date(space.feeRefundedAt).toLocaleDateString()}` : ''}>
                                                    <Chip
                                                        label={paymentStatus}
                                                        size='small'
                                                        sx={{ bgcolor: PAYMENT_COLOR[paymentStatus].bg, color: PAYMENT_COLOR[paymentStatus].color, fontWeight: 600, fontSize: '0.75rem' }}
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={status === 'Rejected' && space.rejectionReason ? space.rejectionReason : ''}>
                                                    <Chip
                                                        label={status}
                                                        size='small'
                                                        sx={{ bgcolor: STATUS_COLOR[status].bg, color: STATUS_COLOR[status].color, fontWeight: 600, fontSize: '0.75rem' }}
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {space.trafficRating ? (
                                                    <Box>
                                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                                            {TRAFFIC_DOT[space.trafficRating]} {space.trafficRating}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                                            Verified by SpotPool{space.verifiedAt ? ` · ${new Date(space.verifiedAt).toLocaleDateString()}` : ''}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                                        {status === 'Draft' ? '—' : 'Awaiting SpotPool Verification'}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {['Awaiting Verification', 'Verification Assigned'].includes(status) && (
                                                    <Tooltip title={space.assignedVerifierName ? `Reassign (currently ${space.assignedVerifierName})` : 'Assign Verifier'}>
                                                        <IconButton
                                                            size='small'
                                                            onClick={() => openAssignVerifier(space)}
                                                            disabled={updatingId === space._id}
                                                            sx={{ color: 'primary.main' }}
                                                        >
                                                            <PersonAddAlt1 fontSize='small' />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {status === 'Verification Completed' && (
                                                    <Tooltip title='Review Findings'>
                                                        <IconButton
                                                            size='small'
                                                            onClick={() => setState(s => ({ ...s, reviewingSpace: space, verificationRejectMode: false, verificationRejectionReason: '' }))}
                                                            disabled={updatingId === space._id}
                                                            sx={{ color: '#854d0e' }}
                                                        >
                                                            <RateReview fontSize='small' />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {status === 'Pending Review' && (
                                                    <>
                                                        <Tooltip title={canApprove ? 'Approve & Publish' : 'The KES 2,000 listing fee has not been paid yet'}>
                                                            <span>
                                                                <IconButton
                                                                    size='small'
                                                                    onClick={() => runModeration('approveSpace', space._id)}
                                                                    disabled={updatingId === space._id || !canApprove}
                                                                    sx={{ color: '#16a34a' }}
                                                                >
                                                                    {updatingId === space._id ? <CircularProgress size={14} color='inherit' /> : <CheckCircleOutline fontSize='small' />}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title='Reject'>
                                                            <IconButton
                                                                size='small'
                                                                onClick={() => setState(s => ({ ...s, rejectingSpace: space, rejectionReason: '' }))}
                                                                disabled={updatingId === space._id}
                                                                sx={{ color: '#ef4444', ml: 0.5 }}
                                                            >
                                                                <CancelOutlined fontSize='small' />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                                {status === 'Published' && (
                                                    <Tooltip title='Unpublish'>
                                                        <IconButton
                                                            size='small'
                                                            onClick={() => runModeration('unpublishSpace', space._id)}
                                                            disabled={updatingId === space._id}
                                                            sx={{ color: '#6b7280' }}
                                                        >
                                                            {updatingId === space._id ? <CircularProgress size={14} color='inherit' /> : <VisibilityOffOutlined fontSize='small' />}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {status === 'Unpublished' && (
                                                    <Tooltip title='Publish'>
                                                        <IconButton
                                                            size='small'
                                                            onClick={() => runModeration('publishSpace', space._id)}
                                                            disabled={updatingId === space._id}
                                                            sx={{ color: 'primary.main' }}
                                                        >
                                                            {updatingId === space._id ? <CircularProgress size={14} color='inherit' /> : <VisibilityOutlined fontSize='small' />}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title='Edit'>
                                                    <IconButton size='small' onClick={() => openEdit(space)} sx={{ color: 'primary.main', ml: 0.5 }}>
                                                        <Edit fontSize='small' />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Show.Else>
            </Show>

            {/* Edit Space Dialog - admin edits details only, never payment/approval state directly */}
            <Dialog open={showModal} onClose={() => !saving && setState(s => ({ ...s, showModal: false }))} fullWidth maxWidth='sm'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Edit Space</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField
                        label='Title'
                        value={form.title}
                        onChange={e => setForm({ title: e.target.value })}
                        fullWidth
                        size='small'
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label='Location'
                            value={form.location}
                            onChange={e => setForm({ location: e.target.value })}
                            fullWidth
                            size='small'
                            placeholder='e.g. Nairobi, Kenya'
                        />
                        <TextField
                            label='Size (sq ft)'
                            type='number'
                            value={form.sizeValue}
                            onChange={e => setForm({ sizeValue: e.target.value })}
                            fullWidth
                            size='small'
                            inputProps={{ min: 0 }}
                        />
                    </Box>
                    <TextField
                        label='Monthly Rent'
                        type='number'
                        value={form.monthlyRent}
                        onChange={e => setForm({ monthlyRent: e.target.value })}
                        fullWidth
                        size='small'
                        inputProps={{ min: 0 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', fontWeight: 600 }}>
                                        {currency}
                                    </Typography>
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        label='Description'
                        value={form.description}
                        onChange={e => setForm({ description: e.target.value })}
                        fullWidth
                        size='small'
                        multiline
                        rows={3}
                    />
                    <TextField
                        label='Amenities & Details'
                        value={form.amenities}
                        onChange={e => setForm({ amenities: e.target.value })}
                        fullWidth
                        size='small'
                        multiline
                        rows={2}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControlLabel
                            control={<Switch checked={form.isAvailable} onChange={e => setForm({ isAvailable: e.target.checked })} />}
                            label='Available'
                        />
                        <TextField
                            label='Availability Note'
                            value={form.availabilityNote}
                            onChange={e => setForm({ availabilityNote: e.target.value })}
                            fullWidth
                            size='small'
                            placeholder='e.g. From 1 November'
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setState(s => ({ ...s, showModal: false }))} disabled={saving}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={saveSpace}
                        disabled={!canSave}
                        startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        Update
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reject Dialog */}
            <Dialog open={Boolean(rejectingSpace)} onClose={() => !updatingId && setState(s => ({ ...s, rejectingSpace: null }))} fullWidth maxWidth='xs'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Reject Listing</DialogTitle>
                <DialogContent sx={{ pt: '12px !important' }}>
                    <Typography sx={{ fontSize: 13, color: '#6b7280', mb: 1.5 }}>
                        The owner will see this reason on "{rejectingSpace?.title}".
                        {rejectingSpace?.paymentStatus === 'Paid' && ' Their KES 2,000 listing fee will be refunded to their balance automatically.'}
                    </Typography>
                    <TextField
                        label='Reason for rejection'
                        value={rejectionReason}
                        onChange={e => setState(s => ({ ...s, rejectionReason: e.target.value }))}
                        fullWidth
                        size='small'
                        multiline
                        rows={2}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setState(s => ({ ...s, rejectingSpace: null }))} disabled={Boolean(updatingId)}>Back</Button>
                    <Button
                        variant='contained'
                        color='error'
                        onClick={confirmReject}
                        disabled={!rejectionReason.trim() || Boolean(updatingId)}
                        startIcon={updatingId ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        Confirm Reject
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Assign Verifier Dialog - same MarketplaceStaff roster/pattern as Orders/index.tsx's
                Assign Staff dialog, reused here for the in-person verification visit. */}
            <Dialog open={Boolean(assigningSpace)} onClose={() => !assigning && setState(s => ({ ...s, assigningSpace: null }))} fullWidth maxWidth='xs'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Assign Verifier</DialogTitle>
                <DialogContent sx={{ pt: '12px !important' }}>
                    <Typography sx={{ fontSize: 13, color: '#6b7280', mb: 1.5 }}>
                        Pick a SpotPool representative to visit "{assigningSpace?.title}" in person and assess its player traffic.
                    </Typography>
                    <Autocomplete
                        options={staffOptions}
                        getOptionLabel={(o) => o.name}
                        value={selectedStaff}
                        onChange={(_, val) => setState(s => ({ ...s, selectedStaff: val }))}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        noOptionsText='No active staff members'
                        renderOption={(props, o) => (
                            <li {...props} key={o._id}>
                                <Box>
                                    <Typography sx={{ fontWeight: 'bold', fontSize: 14 }}>{o.name}</Typography>
                                    <Typography sx={{ color: '#727A8A', fontSize: 12 }}>{o.phone}</Typography>
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} label='Representative' placeholder='Search staff...' />
                        )}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setState(s => ({ ...s, assigningSpace: null }))} disabled={assigning}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={confirmAssignVerifier}
                        disabled={!selectedStaff || assigning}
                        startIcon={assigning ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        Assign
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Review Findings Dialog - the rep's submitted notes/photos/rating, at
                'Verification Completed'. Approve advances the listing to Pending Review (still one
                more admin check, same as every other listing); Reject reuses the reason-textfield
                pattern from the listing Reject Dialog above, but sends the SAME rep back to revise
                rather than reverting the listing itself. */}
            <Dialog open={Boolean(reviewingSpace)} onClose={() => !updatingId && setState(s => ({ ...s, reviewingSpace: null, verificationRejectMode: false }))} fullWidth maxWidth='sm'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Verification Findings</DialogTitle>
                <DialogContent sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography sx={{ fontSize: 13, color: '#6b7280' }}>
                        "{reviewingSpace?.title}" · visited {reviewingSpace?.verifiedAt ? new Date(reviewingSpace.verifiedAt).toLocaleString() : '—'}
                        {reviewingSpace?.assignedVerifierName ? ` · by ${reviewingSpace.assignedVerifierName}` : ''}
                    </Typography>
                    <Chip
                        label={reviewingSpace?.trafficRating ? `${TRAFFIC_DOT[reviewingSpace.trafficRating]} ${reviewingSpace.trafficRating}` : 'No rating submitted'}
                        sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
                    />
                    <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
                        {reviewingSpace?.verificationNotes || 'No notes submitted.'}
                    </Typography>
                    {!!reviewingSpace?.verificationPhotos?.length && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {reviewingSpace.verificationPhotos.map((src, i) => (
                                <Box
                                    key={i}
                                    component='img'
                                    src={src}
                                    alt={`Evidence ${i + 1}`}
                                    sx={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 2, border: '1px solid #e5e7eb' }}
                                />
                            ))}
                        </Box>
                    )}
                    {verificationRejectMode && (
                        <TextField
                            label='Reason for sending back'
                            value={verificationRejectionReason}
                            onChange={e => setState(s => ({ ...s, verificationRejectionReason: e.target.value }))}
                            fullWidth
                            size='small'
                            multiline
                            rows={2}
                            autoFocus
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    {verificationRejectMode ? (
                        <>
                            <Button onClick={() => setState(s => ({ ...s, verificationRejectMode: false, verificationRejectionReason: '' }))} disabled={Boolean(updatingId)}>Back</Button>
                            <Button
                                variant='contained'
                                color='error'
                                onClick={confirmRejectVerification}
                                disabled={!verificationRejectionReason.trim() || Boolean(updatingId)}
                                startIcon={updatingId ? <CircularProgress size={16} color='inherit' /> : undefined}
                            >
                                Send Back
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={() => setState(s => ({ ...s, reviewingSpace: null }))} disabled={Boolean(updatingId)}>Close</Button>
                            <Button color='error' onClick={() => setState(s => ({ ...s, verificationRejectMode: true }))} disabled={Boolean(updatingId)}>
                                Reject
                            </Button>
                            <Button
                                variant='contained'
                                onClick={confirmApproveVerification}
                                disabled={Boolean(updatingId)}
                                startIcon={updatingId ? <CircularProgress size={16} color='inherit' /> : undefined}
                            >
                                Approve
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Image Preview Dialog */}
            <Dialog
                open={Boolean(previewSpace)}
                onClose={() => setState(s => ({ ...s, previewSpace: null }))}
                maxWidth='sm'
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {previewSpace?.title}
                    <IconButton onClick={() => setState(s => ({ ...s, previewSpace: null }))} size='small'>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {previewSpace?.images.map((src, i) => (
                            <Box
                                key={i}
                                component='img'
                                src={src}
                                alt={`${previewSpace.title} ${i + 1}`}
                                sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 2, border: '1px solid #e5e7eb' }}
                            />
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={4000}
                onClose={() => setState(s => ({ ...s, error: null }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity='error' onClose={() => setState(s => ({ ...s, error: null }))} sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default Connect(mapStateToProps)(Spaces)
